import { API_BASE } from '@lib/api/apiConfig';

/**
 * 사용자 로그인 (쿠키 기반)
 *
 * 서버에 로그인 요청을 보내고, JWT 토큰은 HttpOnly 쿠키로 자동 저장됩니다.
 * 클라이언트는 토큰을 직접 다루지 않으며, 인증 요청 시 쿠키를 자동으로 포함합니다.
 *
 * @param {Object} payload 로그인 데이터
 * @param {string} payload.user_id 사용자 ID 또는 이메일
 * @param {string} payload.password 비밀번호
 * @returns {Promise<Object>} 로그인 성공 정보
 * - token_type (string): 토큰 타입 ("bearer")
 * - expires_in (number): access_token 만료 시간 (초 단위)
 *
 * @throws {Error} 로그인 실패 시 예외를 던집니다.
 */
export async function login({ user_id, password }) {
    try {
        const response = await fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include', // ✅ 쿠키를 요청/응답에 포함
            body: JSON.stringify({ user_id, password })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail?.message || '로그인 실패');
        }

        const data = await response.json();
        return data.data; // { token_type, expires_in }
    } catch (err) {
        console.error('Login error:', err);
        throw err;
    }
}

/**
 * 사용자 로그아웃 (쿠키 기반)
 *
 * 서버에 로그아웃 요청을 보내어 HttpOnly 쿠키를 삭제합니다.
 * 클라이언트는 쿠키에 직접 접근하지 않으며, 서버가 삭제합니다.
 *
 * @returns {Promise<void>} 성공 시 아무 값도 반환하지 않음
 * @throws {Error} 실패 시 예외를 던짐
 */
export async function logout() {
    try {
        const response = await fetch(`${API_BASE}/auth/logout`, {
            method: 'POST',
            credentials: 'include' // 쿠키 전송 필수
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail?.message || '로그아웃 실패');
        }

        return; // 성공 시 아무것도 반환하지 않음
    } catch (err) {
        console.error('Logout error:', err);
        throw err;
    }
}